import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { collection, deleteDoc, doc, onSnapshot, query, updateDoc, setDoc } from "firebase/firestore";
import { sendPasswordResetEmail } from "firebase/auth";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, auth, storage, saveLog } from "../lib/firebase";

import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import Navbar from "../components/ui/Navbar"; 
import AdminBar from "../components/ui/adminbar";
import { toast } from "sonner";

import {
  UserCog, Search, Save, Ban, Trash2, Hash, Briefcase, MapPin, 
  Phone, FileText, User, Mail, KeyRound, Loader2, Lock,
  Camera, XCircle, Wrench, CheckCircle2, PlusCircle,
  Image as ImageIcon, ExternalLink, ShieldCheck 
} from "lucide-react";

export default function AdminTecs() {
  const navigate = useNavigate();
  const { userProfile, authLoading } = useAuth();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedTec, setSelectedTec] = useState(null);
  const [users, setUsers] = useState([]);
  const [allDbFunctions, setAllDbFunctions] = useState([]);
  const [searchFunc, setSearchFunc] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [validatingCep, setValidatingCep] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const onlyNumbers = (val = "") => String(val || "").replace(/\D/g, "");
  
  const formatPhone = (v) => {
    v = onlyNumbers(v).substring(0, 11);
    if (v.length > 10) return v.replace(/^(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
    if (v.length > 5) return v.replace(/^(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3");
    return v;
  };

  const formatCpfCnpj = (val = "") => {
    let v = onlyNumbers(val).slice(0, 14);
    if (v.length <= 11) return v.replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d{1,2})$/, "$1-$2");
    return v.replace(/^(\d{2})(\d)/, "$1.$2").replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3").replace(/\.(\d{3})(\d)/, ".$1/$2").replace(/(\d{4})(\d)/, "$1-$2");
  };

  const handleResetPassword = async (email) => {
    if (!email) return toast.error("E-mail não encontrado.");
    try {
      await sendPasswordResetEmail(auth, email);
      toast.success(`E-mail de recuperação enviado para ${email}`);
      await saveLog(userProfile, "RESET SENHA", `Admin disparou reset de senha para ${email}`);
    } catch (error) {
      console.error(error);
      toast.error("Erro ao enviar reset.");
    }
  };

  useEffect(() => {
    const qUsers = query(collection(db, "users"));
    const unsubUsers = onSnapshot(qUsers, (snap) => {
      setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });

    const qFuncs = query(collection(db, "functions"));
    const unsubFuncs = onSnapshot(qFuncs, (snap) => {
        setAllDbFunctions(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(f => f.active !== false));
    });

    return () => { unsubUsers(); unsubFuncs(); };
  }, []);

  const handleCepBlur = async (id, cep) => {
    const cleanCep = onlyNumbers(cep);
    if (cleanCep.length !== 8) return;
    setValidatingCep(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data = await res.json();
      if (!data.erro) {
        setUsers(prev => prev.map(u => {
          if (u.id === id) {
            const pData = u.professionalData || {};
            return { ...u, professionalData: { ...pData, address: { ...(pData.address || {}), cep: cleanCep, street: data.logradouro.toUpperCase(), neighborhood: data.bairro.toUpperCase(), city: data.localidade.toUpperCase(), state: data.uf.toUpperCase() } } };
          }
          return u;
        }));
      }
    } finally { setValidatingCep(false); }
  };

  const handlePhotoUpload = async (tecId, file) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const storageRef = ref(storage, `users/${tecId}/profile.jpg`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      await updateDoc(doc(db, "users", tecId), { "professionalData.photoUrl": url, photoURL: url });
      toast.success("Foto atualizada!");
    } catch (e) { toast.error("Erro no upload."); } finally { setUploadingPhoto(false); }
  };

  const handleToggleTechFunction = async (tec, funcName) => {
    const currentAreas = tec.professionalData?.areas || tec.areas || [];
    let newAreas = currentAreas.includes(funcName) 
      ? currentAreas.filter(a => a !== funcName) 
      : [...currentAreas, funcName];

    try {
        await updateDoc(doc(db, "users", tec.id), {
            "professionalData.areas": newAreas,
            "areas": newAreas 
        });
    } catch (e) { toast.error("Erro ao atualizar função."); }
  };

  const filteredTecs = useMemo(() => {
    const tecsOnly = users.filter(u => u.userType?.toLowerCase() === 'technician' || u.isTechnician === true);
    const term = search.toLowerCase().trim();
    return tecsOnly.filter(t => {
      const isBlocked = t.isBlocked || t.blocked || false;
      const matchesStatus = statusFilter === "all" ? true : statusFilter === "blocked" ? isBlocked : !isBlocked;
      if (!matchesStatus) return false;
      if (!term) return true;
      const tecName = (t.professionalData?.name || t.name || "").toLowerCase();
      return tecName.includes(term) || t.email.toLowerCase().includes(term);
    }).sort((a, b) => (a.professionalData?.name || "").localeCompare(b.professionalData?.name || ""));
  }, [users, search, statusFilter]);

  const handleUpdateField = (id, field, value, isAddress = false) => {
    setUsers(prev => prev.map(u => {
      if (u.id === id) {
        const pData = u.professionalData || {};
        if (isAddress) return { ...u, professionalData: { ...pData, address: { ...(pData.address || {}), [field]: value } } };
        return { ...u, professionalData: { ...pData, [field]: value } };
      }
      return u;
    }));
  };

  const handleSave = async (tec) => {
    const pData = tec.professionalData || {};
    try {
      await updateDoc(doc(db, "users", tec.id), { 
        professionalData: pData, 
        phone: onlyNumbers(pData.phone), 
        updatedAt: new Date().toISOString() 
      });
      await saveLog(userProfile, "UPDATE LIVE", `Dados de ${pData.name} atualizados.`);
      toast.success("Sincronizado!");
    } catch (e) { toast.error("Erro ao salvar."); }
  };

  if (authLoading) return <div className="h-screen flex items-center justify-center font-black text-slate-400">CARREGANDO...</div>;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <div className="flex pt-[73px]">
        <AdminBar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
        <main className="flex-1 p-6 lg:p-10 max-w-7xl mx-auto w-full text-left">
          <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <h1 className="text-4xl font-black text-slate-900 uppercase italic tracking-tighter">Central <span className="text-orange-500">Live</span> Técnicos</h1>
            <div className="relative w-full md:w-96 group text-left">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={20} />
                <input type="text" placeholder="BUSCAR..." className="w-full h-14 pl-12 pr-6 bg-white border-none shadow-xl rounded-2xl font-bold text-sm uppercase italic outline-none" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </header>
          <div className="grid gap-4">
            {filteredTecs.map((tec) => {
              const isOpen = selectedTec === tec.id;
              const pData = tec.professionalData || {};
              const tAddr = pData.address || {};
              const tAreas = pData.areas || tec.areas || [];
              return (
                <Card key={tec.id} className={`rounded-[2rem] border-none shadow-xl transition-all ${isOpen ? 'ring-2 ring-orange-500 scale-[1.01]' : ''}`}>
                  <CardContent className="p-0 text-left">
                    <div className="p-6 flex flex-col md:flex-row items-center justify-between gap-6">
                      <div className="flex items-center gap-5">
                        <div className="w-16 h-16 rounded-2xl bg-slate-100 overflow-hidden flex items-center justify-center relative border-2 border-white shadow-md">
                          {pData.photoUrl ? <img src={pData.photoUrl} className="w-full h-full object-cover" alt="" /> : <UserCog className="text-slate-300" />}
                          {uploadingPhoto && selectedTec === tec.id && <div className="absolute inset-0 bg-black/50 flex items-center justify-center"><Loader2 className="animate-spin text-white" size={16}/></div>}
                        </div>
                        <div className="text-left flex-1">
                          <h3 className="text-xl font-black text-slate-900 uppercase italic tracking-tighter">{pData.name || "Pendente"}</h3>
                          <p className="text-[10px] font-bold text-slate-400 uppercase italic">{tec.email}</p>
                        </div>
                      </div>
                      <Button onClick={() => setSelectedTec(isOpen ? null : tec.id)} className="h-12 rounded-xl font-black uppercase text-[10px] bg-slate-900 text-white shadow-lg">{isOpen ? "Fechar" : "Gerenciar Perfil"}</Button>
                    </div>
                    {isOpen && (
                      <div className="p-8 bg-white border-t border-slate-50 animate-in slide-in-from-top-2 text-left">
                        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-10">
                            <div className="lg:col-span-1 flex flex-col items-center p-6 bg-slate-50 rounded-[2rem] border border-slate-100 relative text-center">
                                <div className="w-32 h-32 rounded-3xl overflow-hidden border-4 border-white shadow-xl relative group">
                                    {pData.photoUrl ? <img src={pData.photoUrl} className="w-full h-full object-cover" alt="" /> : <User size={48} className="m-auto mt-8 text-slate-200" />}
                                    <label className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-all flex flex-col items-center justify-center cursor-pointer text-white text-[8px] font-black uppercase text-center">
                                        <Camera size={20} className="mb-1" /> Mudar Foto
                                        <input type="file" className="hidden" accept="image/*" onChange={(e) => handlePhotoUpload(tec.id, e.target.files[0])} />
                                    </label>
                                </div>
                                <Button onClick={() => handleResetPassword(tec.email)} variant="ghost" className="mt-4 text-[8px] font-black uppercase italic text-slate-400 hover:text-red-500 gap-2"><KeyRound size={12}/> Reset de Senha</Button>
                            </div>
                            <div className="lg:col-span-3 p-6 bg-slate-900 rounded-[2.5rem] text-white relative overflow-hidden text-left">
                                <div className="relative z-10 flex flex-col h-full text-left">
                                    <div className="flex items-center justify-between mb-4">
                                        <h4 className="text-[10px] font-black uppercase text-orange-400 flex items-center gap-2 italic"><Wrench size={14}/> Especialidades</h4>
                                        <div className="relative w-40">
                                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" size={12} />
                                            <input value={searchFunc} onChange={(e) => setSearchFunc(e.target.value)} placeholder="BUSCAR..." className="w-full h-8 pl-8 bg-white/5 border border-white/10 rounded-lg text-[9px] font-black uppercase outline-none" />
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2 overflow-y-auto max-h-32 pr-2 custom-scrollbar text-left">
                                        {allDbFunctions.filter(f => f.name.toLowerCase().includes(searchFunc.toLowerCase())).map((f) => {
                                            const hasFunc = tAreas.includes(f.name);
                                            return (
                                                <button key={f.id} onClick={() => handleToggleTechFunction(tec, f.name)} className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase italic transition-all flex items-center gap-2 ${hasFunc ? 'bg-orange-600 text-white shadow-lg' : 'bg-white/5 text-slate-500 hover:bg-white/10'}`}>
                                                    {hasFunc ? <CheckCircle2 size={12}/> : <PlusCircle size={12}/>}
                                                    {f.name}
                                                </button>
                                            )
                                        })}
                                    </div>
                                </div>
                                <Wrench size={120} className="absolute right-[-20px] bottom-[-20px] text-white/5 rotate-12" />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 text-left">
                          <div className="space-y-4">
                            <Label className="text-[10px] font-black uppercase text-orange-600 flex items-center gap-2 italic"><FileText size={14}/> Dados Profissionais</Label>
                            <div className="space-y-3">
                              <div className="space-y-1"><Label className="text-[9px] font-black text-slate-400 ml-1 flex items-center gap-1"><Mail size={10}/> Login</Label><input readOnly className="w-full h-11 rounded-xl bg-slate-100 border-none px-4 font-bold text-slate-400 italic cursor-not-allowed" value={tec.email || ""} /></div>
                              <div className="space-y-1"><Label className="text-[9px] font-black text-slate-400 ml-1">Nome Fantasia</Label><input className="w-full h-11 rounded-xl border border-slate-100 bg-slate-50 px-4 font-bold uppercase outline-none focus:ring-2 focus:ring-orange-500/20" value={pData.name || ""} onChange={(e) => handleUpdateField(tec.id, "name", e.target.value)} /></div>
                              <div className="space-y-1"><Label className="text-[9px] font-black text-slate-400 ml-1">CPF/CNPJ</Label><input className="w-full h-11 rounded-xl border border-slate-100 bg-slate-50 px-4 font-bold outline-none" value={formatCpfCnpj(pData.document || "")} onChange={(e) => handleUpdateField(tec.id, "document", e.target.value)} /></div>
                              <div className="space-y-1"><Label className="text-[9px] font-black text-blue-600 ml-1 italic">WhatsApp</Label><input className="w-full h-11 rounded-xl border border-blue-100 bg-blue-50/30 px-4 font-black text-blue-700 outline-none" value={formatPhone(pData.phone || "")} onChange={(e) => handleUpdateField(tec.id, "phone", e.target.value)} /></div>
                            </div>
                          </div>
                          <div className="space-y-4">
                            <Label className="text-[10px] font-black uppercase text-blue-600 flex items-center gap-2 italic"><MapPin size={14}/> Localização</Label>
                            <div className="space-y-3">
                              <div className="relative">
                                <Label className="text-[9px] font-black text-slate-400 ml-1">CEP</Label>
                                <input className="w-full h-11 rounded-xl border border-slate-100 bg-slate-50 px-4 font-bold text-center outline-none focus:ring-2 focus:ring-blue-500/20" value={tAddr.cep || ""} onBlur={(e) => handleCepBlur(tec.id, e.target.value)} onChange={(e) => handleUpdateField(tec.id, "cep", e.target.value, true)} />
                                {validatingCep && <Loader2 className="absolute right-3 top-7 animate-spin text-blue-500" size={14} />}
                              </div>
                              <div className="space-y-1 text-left"><Label className="text-[9px] font-black text-slate-400 ml-1">Rua / Número</Label><div className="flex gap-2"><input className="flex-1 h-11 rounded-xl border border-slate-100 bg-slate-50 px-4 font-bold outline-none" value={tAddr.street || ""} onChange={(e) => handleUpdateField(tec.id, "street", e.target.value, true)} /><input className="w-20 h-11 rounded-xl border border-slate-100 bg-slate-50 px-2 text-center font-bold outline-none" placeholder="Nº" value={tAddr.number || ""} onChange={(e) => handleUpdateField(tec.id, "number", e.target.value, true)} /></div></div>
                              <div className="grid grid-cols-2 gap-2 text-left">
                                <div className="space-y-1"><Label className="text-[9px] font-black text-slate-400 ml-1">Complemento</Label><input className="w-full h-11 rounded-xl border border-slate-100 bg-slate-50 px-4 font-bold outline-none text-xs" value={tAddr.complement || ""} onChange={(e) => handleUpdateField(tec.id, "complement", e.target.value, true)} /></div>
                                <div className="space-y-1"><Label className="text-[9px] font-black text-slate-400 ml-1">Bairro</Label><input readOnly className="w-full h-11 rounded-xl bg-slate-100 border-none px-4 font-bold text-slate-400 text-[10px]" value={tAddr.neighborhood || ""} /></div>
                              </div>
                            </div>
                          </div>
                          <div className="space-y-4">
                            <Label className="text-[10px] font-black uppercase text-slate-400 flex items-center gap-2 italic"><FileText size={14}/> Bio Profissional</Label>
                            <textarea className="w-full h-36 rounded-[2rem] border border-slate-100 bg-slate-50 p-6 font-bold text-xs italic outline-none focus:ring-2 ring-orange-500/20 transition-all" value={pData.bio || ""} onChange={(e) => handleUpdateField(tec.id, "bio", e.target.value)} />
                            <Button onClick={() => handleSave(tec)} className="w-full h-16 bg-orange-600 hover:bg-orange-700 text-white font-black rounded-2xl uppercase italic text-xs gap-3 shadow-xl mt-2 transition-all active:scale-95">
                               <Save size={20} /> SALVAR ALTERAÇÕES LIVE
                            </Button>
                          </div>
                        </div>
                        <div className="flex justify-between items-center pt-8 border-t mt-8 text-left">
                             <div className="flex gap-2">
                                {['documentFrontUrl', 'documentBackUrl', 'selfieUrl', 'criminalRecordUrl'].map(k => (
                                    <button key={k} onClick={() => tec.verificationDocs?.[k] && window.open(tec.verificationDocs[k], '_blank')} className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${tec.verificationDocs?.[k] ? 'bg-green-100 text-green-600 hover:bg-green-600 hover:text-white' : 'bg-slate-100 text-slate-300 cursor-not-allowed'}`}>
                                        <ImageIcon size={14}/>
                                    </button>
                                ))}
                             </div>
                            <Button variant="destructive" onClick={() => { if(window.confirm("Excluir?")) deleteDoc(doc(db, "users", tec.id)) }} className="h-14 w-14 rounded-2xl shadow-lg active:scale-95 transition-all"><Trash2 size={20} /></Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </main>
      </div>
      <style dangerouslySetInnerHTML={{ __html: `.custom-scrollbar::-webkit-scrollbar { width: 4px; } .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 10px; }` }} />
    </div>
  );
}